import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAllConsumerUserList,
  getAllGeneratorUserList,
  getAllPermissionList,
  getAllPermissionUserList,
  getCompanyList,
  getConsumerDetails,
  getConsumersList,
  getDiscomsList,
  getGeneratorDetails,
  getGeneratorList,
} from "../../Services/CompanyAdmin/GetApiAdmin";

export const fetchGenerators = createAsyncThunk(
  "generators/fetch",
  async (_, { rejectWithValue }) => {
    try {
      let data = await getGeneratorList();
      return { generators: data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching generators");
    }
  }
);

export const fetchConsumers = createAsyncThunk(
  "consumers/fetch",
  async (_, { rejectWithValue }) => {
    try {
      let data = await getConsumersList();
      return { consumers: data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching consumers");
    }
  }
);

export const fetchDiscoms = createAsyncThunk(
  "discoms/fetch",
  async (_, { rejectWithValue }) => {
    try {
      let data = await getDiscomsList();
      return { discoms: data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching discoms");
    }
  }
);

export const fetchCompany = createAsyncThunk(
  "company/fetch",
  async (_, { rejectWithValue }) => {
    try {
      let data = await getCompanyList();
      return { company: data };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching company");
    }
  }
);
export const fetchConsumerUsers = createAsyncThunk(
  "companyUser/fetchConsumerUsers",
  async (consumerId, { rejectWithValue }) => {
    try {
      const data = await getAllConsumerUserList(consumerId);
      return { consumerUsers: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching consumer users"
      );
    }
  }
);

export const fetchGeneratorUsers = createAsyncThunk(
  "companyUser/fetchGeneratorUsers",
  async (generatorId, { rejectWithValue }) => {
    try {
      const data = await getAllGeneratorUserList(generatorId);
      return { generatorUsers: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching generator users"
      );
    }
  }
);

export const fetchUserPermissions = createAsyncThunk(
  "companyUser/fetchUserPermissions",
  async (userId, { rejectWithValue }) => {
    try {
      const data = await getAllPermissionUserList(userId);
      return { permissions: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching permissions"
      );
    }
  }
);

export const fetchAllPermissions = createAsyncThunk(
  "companyUser/fetchAllPermissions",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAllPermissionList();
      return { permissions: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching permissions"
      );
    }
  }
);

export const fetchConsumer = createAsyncThunk(
  "consumer/fetch",
  async (consumerId, { rejectWithValue }) => {
    try {
      const data = await getConsumerDetails(consumerId);
      return { consumerDetail: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching consumer details"
      );
    }
  }
);

export const fetchGeneratorDetails = createAsyncThunk(
  "generator/fetchGeneratorDetails",
  async (generatorId, { rejectWithValue }) => {
    try {
      const data = await getGeneratorDetails(generatorId);
      return { generatorDetail: data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error fetching generator details"
      );
    }
  }
);

const companyAdminSlice = createSlice({
  name: "companyAdmin",
  initialState: {
    generators: null,
    consumers: null,
    discoms: null,
    consumerUsers: null,
    generatorUsers: null,
    permissions: null,
    permissionsList: null,
    consumerDetail: null,
    generatorDetail: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGenerators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGenerators.fulfilled, (state, action) => {
        state.loading = false;
        state.generators = action.payload?.generators?.data?.data || [];
      })
      .addCase(fetchGenerators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchConsumers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsumers.fulfilled, (state, action) => {
        state.loading = false;
        state.consumers = action.payload?.consumers?.data?.data || [];
      })
      .addCase(fetchConsumers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchDiscoms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDiscoms.fulfilled, (state, action) => {
        state.loading = false;
        state.discoms = action.payload?.discoms?.data?.data || [];
      })
      .addCase(fetchDiscoms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchConsumerUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsumerUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.consumerUsers = action.payload?.consumerUsers?.data?.data || [];
      })
      .addCase(fetchConsumerUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchGeneratorUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGeneratorUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.generatorUsers = action.payload?.generatorUsers?.data?.data || [];
      })
      .addCase(fetchGeneratorUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload?.permissions?.data?.data || [];
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchAllPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissionsList = action.payload?.permissions?.data?.data || [];
      })
      .addCase(fetchAllPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchConsumer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsumer.fulfilled, (state, action) => {
        state.loading = false;
        state.consumerDetail = action.payload?.consumerDetail?.data || null;
      })
      .addCase(fetchConsumer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    builder
      .addCase(fetchGeneratorDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGeneratorDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.generatorDetail = action.payload?.generatorDetail?.data || null;
      })
      .addCase(fetchGeneratorDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default companyAdminSlice.reducer;
